import type { Guide, GuideRecordHint } from '../types';

/**
 * S-21.10-12 — automatyczne ponawianie dostarczenia pliku do VIA (scenariusz e2e 20, krok 20.2; ścieżki ADM-02,
 * MIX-06). Rekord: zlecenie C4 z `scenario-state.mjs` (dane pod slajdy z 22.10.2026) — plik po eksporcie „nie
 * dotarł”, automat ponowił raz bez skutku i czeka na kolejną próbę (próba 2 z 3).
 */
const RETRYING: GuideRecordHint = {
  description: 'prezlecenie u Administracji z plikiem, który nie dotarł — z kolejki „Błąd zapisu — ponawiane automatycznie”',
  listPath: '/pre-orders',
  listFilter: { intakeQueue: 'adm_retrying' },
};

export const guide: Guide = {
  flagKey: 'viaDeliveryAutoRetry',
  title: 'Automatyczne ponawianie dostarczenia pliku do VIA',
  whatItDoes:
    'Gdy plik zlecenia nie dotrze do importu TMS, aplikacja sama ponawia dostarczenie — zawsze tego samego, zatwierdzonego pliku, bez nowej treści — domyślnie 3 razy co 15 minut. W tym czasie zlecenie stoi na pulpicie Administracji w kolejce „Błąd zapisu — ponawiane automatycznie”, a do „Błąd zapisu do VIA” trafia dopiero po wyczerpaniu prób albo gdy ponawianie trzeba przerwać (zmiana danych, podobne zlecenie). Ręczne ponowienie zostaje, a każda próba jest widoczna w panelu zapisu w „Próbach dostarczenia”. Dotąd była jedna próba i błąd od razu trafiał do kolejki.',
  audience: ['Administracja', 'administrator (parametry)'],
  presentationItems: ['S-21.10-12'],
  steps: [
    {
      route: '/intake-dashboard',
      target: 'intake-tile-adm_retrying',
      title: 'Kolejka „Błąd zapisu — ponawiane automatycznie”',
      description:
        'Na pulpicie Administracji: zlecenia, których plik nie dotarł, a aplikacja jeszcze ponawia dostarczenie. Po wyczerpaniu prób zlecenie przechodzi do „Błąd zapisu do VIA” — tam trzeba sprawdzić przyczynę i ponowić ręcznie.',
      action: 'Kliknij kafelek, żeby zobaczyć prezlecenia z tej kolejki.',
      recorder: { account: 'adm' },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: RETRYING,
      target: 'via-delivery-retry',
      mockupTestId: 'adm-bledy-ponow',
      title: 'Ponawianie w panelu „Zapis do VIA”',
      description:
        'Panel mówi, że plik nie dotarł i aplikacja ponawia sama: która to próba, z ilu i kiedy następna. Automat kładzie ten sam zatwierdzony plik — treść się nie zmienia. „Ponów wysłanie do TMS” zostaje dla człowieka.',
      action: 'Otwórz prezlecenie z kolejki i w panelu „Zapis do VIA” kliknij „Sprawdź plik”.',
      recorder: {
        account: 'adm',
        demo: { kind: 'scenario', key: 'c4PreOrderId' },
        actions: [{ kind: 'clickButton', name: 'Sprawdź plik' }],
      },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: RETRYING,
      target: 'via-delivery-attempts',
      title: 'Próby dostarczenia',
      description:
        'Każda próba osobno — eksport, ponowienie człowieka i automatu — z czasem, wynikiem, miejscem dostarczenia i komunikatem błędu. Poprzednie próby nie znikają, więc widać, co działo się z plikiem.',
      action: 'Pod komunikatem o ponawianiu przeczytaj listę „Próby dostarczenia”; gdy próby się skończą — kliknij „Ponów wysłanie do TMS”.',
      recorder: {
        account: 'adm',
        demo: { kind: 'scenario', key: 'c4PreOrderId' },
        actions: [{ kind: 'clickButton', name: 'Sprawdź plik' }],
      },
    },
    {
      route: '/suggestion-parameters',
      target: 'suggestion-parameter-viaDeliveryRetryAttempts',
      title: 'Liczba prób i odstęp',
      description:
        'Ile razy i co ile minut aplikacja ponawia dostarczenie, ustalają dwa parametry „Zapis do VIA” — domyślnie 3 próby co 15 minut (wartości robocze). Zmiana wymaga powodu i trafia do historii; 0 prób oznacza, że błąd od razu idzie do kolejki.',
      action: 'Otwórz „Parametry rekomendacji” w menu (grupa „Administracja”), przy parametrze kliknij „Zmień” i wpisz powód.',
      recorder: { account: 'admin' },
    },
  ],
};
